import { useCallback, useEffect, useRef, useState } from "react";
import api from "@/lib/api";
const AUDIO_PROFILES = { relaxed: [0.075, 3, 9, 11], balanced: [0.05, 2.2, 5, 7], strict: [0.035, 1.7, 3, 5] };

// Real, advisory-only proctoring. Honors the per-assessment policy: webcam (always
// when proctored), microphone (require_mic), and screen recording (require_screen).
// Captures environment events + periodic webcam snapshots and streams them to the
// backend for AI vision analysis. Camera/mic start automatically; screen capture
// needs a user gesture (startScreen) because getDisplayMedia requires one.
const VISUAL = new Set(["no_face", "multiple_faces", "looking_away", "phone_detected", "headphones_detected", "device_detected", "paper_detected", "second_screen"]);
const AUDIO = new Set(["talking", "multiple_voices", "loud_noise"]);
const toB64 = (blob) => new Promise((res) => { const r = new FileReader(); r.onload = () => res(r.result); r.onerror = () => res(null); r.readAsDataURL(blob); });

// Record a short (~5s) audio clip from the exam mic as evidence for a talking flag.
function recordClip(stream, ms = 5000) {
  const tracks = stream?.getAudioTracks?.() || [];
  if (!tracks.length || !window.MediaRecorder) return Promise.resolve(null);
  return new Promise((resolve) => {
    try {
      const mime = ["audio/webm;codecs=opus", "audio/webm", "audio/mp4"].find((m) => MediaRecorder.isTypeSupported(m));
      const rec = new MediaRecorder(new MediaStream(tracks), mime ? { mimeType: mime, audioBitsPerSecond: 32000 } : {});
      const parts = [];
      rec.ondataavailable = (e) => { if (e.data?.size) parts.push(e.data); };
      rec.onstop = () => (parts.length ? toB64(new Blob(parts, { type: rec.mimeType || "audio/webm" })).then(resolve) : resolve(null));
      rec.start();
      setTimeout(() => { try { rec.stop(); } catch { resolve(null); } }, ms);
    } catch { resolve(null); }
  });
}

export function useProctoring(candId, { enabled, policy = {}, trialId, snapshotInterval = 4000, analyzeEvery = 5 } = {}) {
  const requireCamera = policy.require_camera !== false;
  const requireMic = !!policy.require_mic;
  const requireScreen = !!policy.require_screen;
  const lockdown = !!policy.lockdown;
  const clipBusyRef = useRef(false);

  const videoRef = useRef(null);
  const screenRef = useRef(null);
  const streamRef = useRef(null);
  const screenStreamRef = useRef(null);
  const canvasRef = useRef(null);
  const lastRef = useRef({});
  const seqRef = useRef(0);
  const recorderRef = useRef(null);
  const chunksRef = useRef([]);
  const screenRecorderRef = useRef(null);
  const screenChunksRef = useRef([]);
  const faceDetectorRef = useRef(undefined);
  const faceStreakRef = useRef(0);
  const audioCtxRef = useRef(null);
  const audioTimerRef = useRef(null);
  const [events, setEvents] = useState([]);
  const [status, setStatus] = useState("idle"); // idle | active | error
  const [screenStatus, setScreenStatus] = useState("off");
  const [micStatus, setMicStatus] = useState("off");
  const [deviceErrors, setDeviceErrors] = useState({});
  const [audioLevel, setAudioLevel] = useState(0);
  const [audioSuspended, setAudioSuspended] = useState(false);
  const [recording, setRecording] = useState(false);
  const [uploadStatus, setUploadStatus] = useState("");
  const [retry, setRetry] = useState(0);
  const recordingClosedRef = useRef(false);
  const startedAtRef = useRef(null);
  const screenStartedAtRef = useRef(null);
  const analysisBusyRef = useRef(false);
  const sensitivity = policy.sensitivity || "balanced";
  const audioParams = AUDIO_PROFILES[sensitivity];
  const retryDevices = () => setRetry((n) => n + 1);
  const resumeAudio = async () => { try { await audioCtxRef.current?.resume(); setAudioSuspended(audioCtxRef.current?.state !== "running"); } catch { setAudioSuspended(true); } };

  const pushEvent = useCallback(
    (type, detail) => {
      const now = Date.now();
      if (lastRef.current[type] && now - lastRef.current[type] < 4000) return; // throttle bursts
      lastRef.current[type] = now;
      const capturedAt = new Date().toISOString();
      setEvents((e) => [{ type, detail, at: capturedAt }, ...e].slice(0, 200));
      if (candId) {
        const v = videoRef.current;
        let image_base64;
        if (VISUAL.has(type) && v && v.videoWidth) {
          const c = document.createElement("canvas");
          c.width = 480; c.height = Math.round((480 * v.videoHeight) / v.videoWidth);
          c.getContext("2d").drawImage(v, 0, 0, c.width, c.height);
          image_base64 = c.toDataURL("image/jpeg", 0.6);
        }
        const send = (audio_base64) => api.post(`/candidates/${candId}/proctor/event`, { type, detail, captured_at: capturedAt, image_base64, audio_base64: audio_base64 || undefined }).catch(() => {});
        if (AUDIO.has(type) && !clipBusyRef.current && streamRef.current?.getAudioTracks().length) {
          clipBusyRef.current = true;
          recordClip(streamRef.current).then(send).finally(() => { clipBusyRef.current = false; });
        } else send();
      }
    },
    [candId]
  );

  // Reflect the policy's screen requirement into the gate status.
  useEffect(() => {
    setScreenStatus((s) => (requireScreen ? (s === "active" ? "active" : "pending") : "off"));
  }, [requireScreen]);

  // In-browser multiple-person detector (native FaceDetector when available).
  const detectFaces = useCallback(async (source) => {
    if (faceDetectorRef.current === null) return;
    if (faceDetectorRef.current === undefined) {
      if (typeof window === "undefined" || !("FaceDetector" in window)) { faceDetectorRef.current = null; return; }
      try {
        // eslint-disable-next-line no-undef
        faceDetectorRef.current = new window.FaceDetector({ fastMode: true, maxDetectedFaces: 5 });
      } catch { faceDetectorRef.current = null; return; }
    }
    try {
      const faces = await faceDetectorRef.current.detect(source);
      faceStreakRef.current = faces?.length > 1 ? faceStreakRef.current + 1 : 0;
      if (faceStreakRef.current >= ({relaxed:3, balanced:2, strict:1}[sensitivity])) { pushEvent("multiple_faces", `${faces.length} people detected across frames`); faceStreakRef.current = 0; }
    } catch { /* detection failed on this frame — ignore */ }
  }, [pushEvent, sensitivity]);

  // In-browser voice / background-noise detection on the exam microphone (advisory, no transcription).
  const startAudioAnalysis = useCallback((stream) => {
    const AC = window.AudioContext || window.webkitAudioContext;
    const tracks = stream.getAudioTracks ? stream.getAudioTracks() : [];
    if (!AC || !tracks.length) return;
    try {
      const ctx = new AC();
      audioCtxRef.current = ctx;
      ctx.resume().then(() => setAudioSuspended(ctx.state !== "running")).catch(() => setAudioSuspended(true));
      const src = ctx.createMediaStreamSource(stream);
      const analyser = ctx.createAnalyser();
      analyser.fftSize = 2048;
      src.connect(analyser);
      const timeBuf = new Uint8Array(analyser.fftSize);
      const freqBuf = new Uint8Array(analyser.frequencyBinCount);
      const sampleRate = ctx.sampleRate || 44100;
      const binHz = sampleRate / analyser.fftSize;
      const lo = Math.max(1, Math.floor(300 / binHz));
      const hi = Math.min(Math.floor(3400 / binHz), freqBuf.length - 1);
      let calib = 0, floor = 0.012, voiceFrames = 0, noiseFrames = 0, loudFrames = 0;
      audioTimerRef.current = setInterval(() => {
        analyser.getByteTimeDomainData(timeBuf);
        let sum = 0;
        for (let i = 0; i < timeBuf.length; i++) { const v = (timeBuf[i] - 128) / 128; sum += v * v; }
        const rms = Math.sqrt(sum / timeBuf.length);
        setAudioLevel(Math.min(100, Math.round(rms * 500)));
        if (ctx.state !== "running") return;
        if (calib < 12) { floor = Math.max(floor, rms); calib++; return; } // ~3s adaptive noise floor
        analyser.getByteFrequencyData(freqBuf);
        let speechSum = 0, totalSum = 0;
        for (let i = 0; i < freqBuf.length; i++) { totalSum += freqBuf[i]; if (i >= lo && i <= hi) speechSum += freqBuf[i]; }
        const speechRatio = totalSum ? speechSum / totalSum : 0;
        loudFrames = rms > 0.35 ? loudFrames + 1 : 0;
        if (loudFrames >= 4) { pushEvent("loud_noise", "A sustained loud noise was picked up"); loudFrames = 0; }
        const loud = rms > Math.max(audioParams[0], Math.min(floor, 0.035) * audioParams[1]);
        if (loud && speechRatio > 0.6) { voiceFrames++; noiseFrames = 0; }
        else if (loud) { noiseFrames++; voiceFrames = Math.max(0, voiceFrames - 1); }
        else { voiceFrames = Math.max(0, voiceFrames - 1); noiseFrames = Math.max(0, noiseFrames - 1); }
        if (voiceFrames >= audioParams[2]) { pushEvent("talking", "Sustained speech-like sound detected; human review required"); voiceFrames = 0; }
        else if (noiseFrames >= audioParams[3]) { pushEvent("background_noise", "Persistent background noise detected"); noiseFrames = 0; }
      }, 250);
    } catch { setDeviceErrors((e)=>({...e,audio:"Microphone monitoring could not start in this browser."})); }
  }, [pushEvent, audioParams]);

  const snapshot = useCallback(() => {
    if (recordingClosedRef.current) return;
    const video = videoRef.current;
    if (!video || video.readyState < 2) return;
    const canvas = canvasRef.current || (canvasRef.current = document.createElement("canvas"));
    canvas.width = 320;
    canvas.height = 240;
    try {
      canvas.getContext("2d").drawImage(video, 0, 0, 320, 240);
      const data = canvas.toDataURL("image/jpeg", 0.55);
      seqRef.current += 1;
      detectFaces(video);
      const analyze = seqRef.current % analyzeEvery === 1;
      const captured_at = new Date().toISOString();
      if (candId) api.post(`/candidates/${candId}/proctor/snapshot`, { image_base64: data, analyze, captured_at }).then(({ data: response }) => { setDeviceErrors((e) => ({...e, upload: null, analysis: response.analysis_unavailable ? "AI frame analysis is temporarily unavailable" : null})); }).catch(() => { if (!recordingClosedRef.current) setDeviceErrors((e) => ({...e, upload: "Frames are not reaching the server. Check your connection."})); });
      if (trialId && analyze && !analysisBusyRef.current) {
        analysisBusyRef.current = true;
        api.post(`/test/trial/${trialId}/proctor/analyze`, { image_base64: data, analyze: true, captured_at }).then(({data: result}) => {
          if (!result.available) setDeviceErrors((e) => ({...e, analysis: "AI frame analysis is temporarily unavailable"}));
          else { setDeviceErrors((e) => ({...e, analysis: null})); result.flags.forEach((f) => pushEvent(f.type, f.detail)); }
        }).catch(() => setDeviceErrors((e) => ({...e, analysis: "Could not analyze this frame; local monitoring continues"}))).finally(() => { analysisBusyRef.current = false; });
      }
    } catch { /* frame not ready */ }
  }, [candId, trialId, analyzeEvery, detectFaces, pushEvent]);

  // Safety net: (re)bind media streams whenever the preview <video> elements mount.
  useEffect(() => {
    if (videoRef.current && streamRef.current && videoRef.current.srcObject !== streamRef.current) {
      videoRef.current.srcObject = streamRef.current;
      videoRef.current.play?.().catch(() => {});
    }
    if (screenRef.current && screenStreamRef.current && screenRef.current.srcObject !== screenStreamRef.current) {
      screenRef.current.srcObject = screenStreamRef.current;
      screenRef.current.play?.().catch(() => {});
    }
  });

  useEffect(() => {
    if (!enabled) return undefined;
    let snapTimer;
    let cancelled = false;

    (async () => {
      recordingClosedRef.current = false;
      setDeviceErrors({}); setRecording(false);
      setStatus(requireCamera ? "checking" : "off"); setMicStatus(requireMic ? "checking" : "off");
      const stream = new MediaStream();
      streamRef.current = stream;
      const acquire = async (kind) => {
        const camera = kind === "camera";
        if (camera ? !requireCamera : !requireMic) return;
        try {
          if (!window.isSecureContext || !navigator.mediaDevices?.getUserMedia) throw Object.assign(new Error(), { name: "Unsupported" });
          const part = await navigator.mediaDevices.getUserMedia(camera ? { video: { width: { ideal: 640 }, height: { ideal: 480 } }, audio: false } : { video: false, audio: { echoCancellation: true, noiseSuppression: true } });
          if (cancelled) { part.getTracks().forEach((t) => t.stop()); return; }
          part.getTracks().forEach((track) => {
            stream.addTrack(track);
            track.addEventListener("ended", () => { (camera ? setStatus : setMicStatus)("error"); setDeviceErrors((e) => ({...e, [kind]: `${camera ? "Camera" : "Microphone"} disconnected. Reconnect and retry.`})); pushEvent(camera ? "camera_blocked" : "mic_blocked", `${kind} disconnected`); });
          });
          (camera ? setStatus : setMicStatus)("active");
          if (camera && videoRef.current) { videoRef.current.srcObject = stream; await videoRef.current.play().catch(() => {}); }
        } catch (e) {
          if (cancelled) return;
          const messages = { NotAllowedError: "Permission denied. Allow this device in your browser's site settings, then retry.", NotFoundError: "No device found. Connect a device, then retry.", NotReadableError: "Device is busy. Close other apps using it, then retry.", Unsupported: "Device access requires a secure browser page. Open this assessment directly in a supported browser." };
          (camera ? setStatus : setMicStatus)("error");
          const message = messages[e.name] || "Could not start this device. Check the connection and retry.";
          setDeviceErrors((old) => ({...old,[kind]:message})); pushEvent(camera ? "camera_blocked" : "mic_blocked", `${kind}: ${message}`);
        }
      };
      await Promise.all([acquire("camera"), acquire("microphone")]);
      if (cancelled) return;
      streamRef.current = stream;
      if (videoRef.current && stream.getVideoTracks().length) { videoRef.current.srcObject = stream; await videoRef.current.play().catch(() => {}); }
      if (stream.getTracks().length) {
        try {
          if (window.MediaRecorder) {
            const candidates = stream.getVideoTracks().length ? ["video/webm;codecs=vp8,opus", "video/webm", "video/mp4"] : ["audio/webm", "audio/mp4"];
            const mime = candidates.find((m) => MediaRecorder.isTypeSupported(m));
            const rec = new MediaRecorder(stream, { ...(mime ? { mimeType: mime } : {}), videoBitsPerSecond: 180000 });
            chunksRef.current = [];
            rec.ondataavailable = (e) => { if (e.data?.size) chunksRef.current.push(e.data); };
            rec.start(5000); startedAtRef.current = new Date().toISOString(); recorderRef.current = rec; setRecording(true);
          } else setDeviceErrors((e) => ({...e, recording: "Full-motion recording is unsupported. Available snapshots are still saved."}));
        } catch { setDeviceErrors((e) => ({...e, recording: "Full-motion recording could not start. Available snapshots are still saved."})); }
        startAudioAnalysis(stream);
        snapTimer = setInterval(snapshot, snapshotInterval);
      }
    })();

    const onVis = () => { if (document.hidden) pushEvent("tab_switch", "Switched away from the assessment tab"); };
    const onBlur = () => pushEvent("focus_loss", "Assessment window lost focus");
    const onFs = () => { if (!document.fullscreenElement) pushEvent("fullscreen_exit", "Exited fullscreen mode"); };
    const onCopy = (e) => { if (lockdown) e.preventDefault(); pushEvent("copy", lockdown ? "Copy was blocked by lockdown mode" : "Copy action during the assessment"); };
    const onPaste = (e) => { if (lockdown) e.preventDefault(); pushEvent("paste", lockdown ? "Paste was blocked by lockdown mode" : "Paste action during the assessment"); };
    const onCtx = (e) => { e.preventDefault(); pushEvent("right_click", "Context menu opened"); };
    const onKey = (e) => {
      if (!lockdown) return;
      const k = (e.key || "").toLowerCase();
      const mod = e.ctrlKey || e.metaKey;
      const shot = k === "printscreen" || (e.metaKey && e.shiftKey && ["3", "4", "5", "s"].includes(k)) || (e.ctrlKey && e.shiftKey && k === "s");
      if (shot || (mod && k === "p")) { e.preventDefault(); pushEvent("screenshot_attempt", shot ? "Screenshot shortcut pressed" : "Print shortcut pressed"); }
      else if (mod && e.shiftKey && ["i", "j", "c"].includes(k)) e.preventDefault();
    };
    const checkScreens = () => { if (window.screen?.isExtended) pushEvent("second_monitor", "More than one monitor is connected"); };
    checkScreens();
    window.screen?.addEventListener?.("change", checkScreens);
    document.addEventListener("cut", onCopy);
    document.addEventListener("keydown", onKey, true);

    document.addEventListener("visibilitychange", onVis);
    window.addEventListener("blur", onBlur);
    document.addEventListener("fullscreenchange", onFs);
    document.addEventListener("copy", onCopy);
    document.addEventListener("paste", onPaste);
    document.addEventListener("contextmenu", onCtx);

    return () => {
      cancelled = true;
      clearInterval(snapTimer);
      document.removeEventListener("visibilitychange", onVis);
      window.removeEventListener("blur", onBlur);
      document.removeEventListener("fullscreenchange", onFs);
      document.removeEventListener("copy", onCopy);
      document.removeEventListener("paste", onPaste);
      document.removeEventListener("contextmenu", onCtx);
      document.removeEventListener("cut", onCopy);
      document.removeEventListener("keydown", onKey, true);
      window.screen?.removeEventListener?.("change", checkScreens);
      if (recorderRef.current && recorderRef.current.state !== "inactive") {
        try { recorderRef.current.stop(); } catch { /* noop */ }
      }
      if (screenRecorderRef.current && screenRecorderRef.current.state !== "inactive") {
        try { screenRecorderRef.current.stop(); } catch { /* noop */ }
      }
      if (audioTimerRef.current) { clearInterval(audioTimerRef.current); audioTimerRef.current = null; }
      if (audioCtxRef.current && audioCtxRef.current.state !== "closed") { try { audioCtxRef.current.close(); } catch { /* noop */ } audioCtxRef.current = null; }
      if (streamRef.current) streamRef.current.getTracks().forEach((t) => t.stop());
      if (screenStreamRef.current) screenStreamRef.current.getTracks().forEach((t) => t.stop());
      setStatus("idle");
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, candId, requireCamera, requireMic, retry, lockdown]);

  // Screen capture — must be triggered by a user gesture (getDisplayMedia requirement).
  const startScreen = useCallback(async () => {
    try {
      const s = await navigator.mediaDevices.getDisplayMedia({ video: true, audio: false });
      screenStreamRef.current = s;
      if (screenRef.current) { screenRef.current.srcObject = s; await screenRef.current.play().catch(() => {}); }
      setScreenStatus("active");
      const track = s.getVideoTracks()[0];
      if (track) track.addEventListener("ended", () => { setScreenStatus("error"); pushEvent("screen_share_stopped", "Screen sharing was stopped during the assessment"); });
      try {
        if (window.MediaRecorder) {
          const mime = ["video/webm;codecs=vp8", "video/webm", "video/mp4"].find((m) => MediaRecorder.isTypeSupported(m));
          const rec = new MediaRecorder(s, { ...(mime ? { mimeType: mime } : {}), videoBitsPerSecond: 400000 });
          screenChunksRef.current = [];
          rec.ondataavailable = (e) => { if (e.data && e.data.size) screenChunksRef.current.push(e.data); };
          rec.start(5000);
          screenStartedAtRef.current = new Date().toISOString();
          screenRecorderRef.current = rec;
        }
      } catch { /* screen recording unsupported */ }
      return true;
    } catch {
      setScreenStatus("error");
      pushEvent("screen_blocked", "Screen sharing was not granted");
      return false;
    }
  }, [pushEvent]);

  // Stop recorders and upload the full-motion WebM(s) (call before leaving the assessment).
  const finalize = useCallback(async () => {
    recordingClosedRef.current = true;
    if (audioTimerRef.current) clearInterval(audioTimerRef.current);
    try { await audioCtxRef.current?.close(); } catch { /* already closed */ }
    const upload = async (recRef, chunksR, kind) => {
      const rec = recRef.current;
      if (!rec) return;
      if (!rec.stoppedAt) rec.stoppedAt = Date.now();
      if (rec.state !== "inactive") await new Promise((res) => { rec.onstop = res; try { rec.stop(); } catch { res(); } });
      (kind === "screen" ? screenStreamRef.current : streamRef.current)?.getTracks().forEach((t) => t.stop());
      setRecording(false);
      const type = rec.mimeType?.split(";")[0] || "video/webm";
      const blob = new Blob(chunksR.current, { type });
      if (blob.size && candId) {
        if (blob.size > 120 * 1024 * 1024) { setUploadStatus("Recording exceeds 120 MB. Snapshots are retained."); throw new Error("Recording too large; contact your reviewer"); }
        const uploadId = rec.uploadId || (rec.uploadId = crypto.randomUUID());
        const count = Math.ceil(blob.size / (512 * 1024));
        try {
          for (let i = 0; i < count; i++) {
            setUploadStatus(`Saving ${kind} recording: ${Math.round(i / count * 100)}%`);
            await api.put(`/candidates/${candId}/proctor/uploads/${uploadId}/chunks/${i}`, blob.slice(i * 512 * 1024, (i+1) * 512 * 1024), { headers: { "Content-Type": "application/octet-stream" } });
          }
          const started_at = kind === "screen" ? screenStartedAtRef.current : startedAtRef.current;
          setUploadStatus(`Finalizing ${kind} recording…`);
          await api.post(`/candidates/${candId}/proctor/uploads/${uploadId}/complete`, { chunks: count, kind, content_type: type, started_at, duration_seconds: (rec.stoppedAt - Date.parse(started_at)) / 1000 });
          setUploadStatus("Recording saved");
        } catch (e) { setUploadStatus("Recording upload failed. Your answers are saved locally; submit again to retry."); throw e; }
      }
      chunksR.current = []; recRef.current = null;
    };
    await upload(recorderRef, chunksRef, "camera");
    await upload(screenRecorderRef, screenChunksRef, "screen");
    if (screenStreamRef.current) { screenStreamRef.current.getTracks().forEach((t) => t.stop()); screenStreamRef.current = null; }
  }, [candId]);

  const needsScreen = requireScreen && screenStatus !== "active";

  return { videoRef, screenRef, events, status, screenStatus, micStatus, audioLevel, audioSuspended, recording, uploadStatus, deviceErrors, retryDevices, resumeAudio, needsScreen, startScreen, finalize };
}
